<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">

      <!-- Back + range selector header -->
      <div class="wad-topbar">
        <button
          type="button"
          class="wad-back"
          @click="$router.back()"
        >
          <i class="bi bi-arrow-left" />
          {{ $t('components.wirepasAssets.backToList') }}
        </button>
        <div class="wad-range-pills">
          <button
            v-for="h in rangeOptions"
            :key="h.v"
            type="button"
            class="wl-pill"
            :class="{ 'wl-pill--active': rangeHours === h.v }"
            @click="setRange(h.v)"
          >
            {{ h.l }}
          </button>
        </div>
      </div>

      <div
        v-if="loading"
        class="wp-loading"
      >
        <span class="spinner-border wp-loading__spinner" role="status" />
        <span class="wp-loading__text">{{ $t('components.wirepasAssets.loading') }}</span>
      </div>

      <div
        v-else-if="error"
        class="alert alert-danger m-3"
      >
        <i class="bi bi-exclamation-triangle-fill me-2" />{{ error }}
      </div>

      <template v-else-if="data">
        <!-- KPI: Lokalizacja | Status | Bateria | W lokalizacji od — identycznie jak Limonfy -->
        <div class="wl-kpis mb-4">
          <div class="wl-stat wl-stat--dark wad-kpi-location">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasAssets.detail.location') }}</span>
              <i class="bi bi-geo-alt wl-stat__icon" />
            </div>
            <span class="wl-stat__value wad-location-name">{{ data.location?.name || '—' }}</span>
            <span v-if="data.location?.gwId" class="wl-stat__meta">{{ data.location.gwId }}</span>
          </div>

          <div class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasAssets.columns.status') }}</span>
              <i class="bi bi-activity wl-stat__icon" />
            </div>
            <span class="wad-status" :class="`wad-status--${data.status}`">
              {{ $t(`components.wirepasAssets.status.${data.status}`) }}
            </span>
          </div>

          <div v-if="data.batteryV !== null" class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasAssets.detail.battery') }}</span>
              <i class="bi bi-battery-half wl-stat__icon" style="color:#f77f01" />
            </div>
            <span class="wl-stat__value">{{ data.batteryV?.toFixed(2) }}<span class="wad-unit">V</span></span>
            <div
              v-if="data.batteryPct !== null"
              class="wl-stat__accent"
              :style="{ width: `${data.batteryPct}%`, background: data.batteryPct < 20 ? '#dc3545' : '#3f8f52' }"
            />
          </div>

          <div class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasAssets.detail.lastSeen') }}</span>
              <i class="bi bi-clock wl-stat__icon" />
            </div>
            <span class="wl-stat__value wad-ago">{{ agoText(data.lastSeen) }}</span>
          </div>

          <div v-if="data.sinceAtLocation" class="wl-stat">
            <div class="wl-stat__head">
              <span class="wl-stat__title">{{ $t('components.wirepasAssets.detail.sinceAtLocation') }}</span>
              <i class="bi bi-clock-history wl-stat__icon" style="color:#4870b8" />
            </div>
            <span class="wl-stat__value wad-ago">{{ durText(data.sinceAtLocation) }}</span>
          </div>
        </div>

        <!-- Main content: Journey (left) + Toggle panel (right) -->
        <div class="wad-main row g-3">
          <!-- Left: Journey sidebar -->
          <div class="col-12 col-lg-4">
            <div class="assets-grid-card h-100">
              <div class="assets-grid-card__body p-3">
                <WirepasJourneySidebar :legs="data.journey ?? []" />
              </div>
            </div>
          </div>

          <!-- Right: Map or Charts -->
          <div class="col-12 col-lg-8">
            <div class="assets-grid-card">
              <div class="assets-grid-card__header">
                <div class="assets-grid-title">
                  <div class="assets-grid-title__icon">
                    <i :class="tracking ? 'bi bi-map' : 'bi bi-graph-up'" />
                  </div>
                  <div>
                    <div class="assets-grid-title__text">
                      {{ tracking ? $t('components.wirepasAssets.detail.routeMap') : data.name || data.serialNumber }}
                    </div>
                    <div class="text-muted small">{{ data.deviceModel }} · {{ data.serialNumber }}</div>
                  </div>
                </div>
                <!-- Toggle tracking mode -->
                <button type="button" class="wad-toggle" :class="{ 'wad-toggle--active': tracking }" @click="tracking = !tracking">
                  <i class="bi bi-map me-1" />
                  {{ $t('components.wirepasAssets.detail.trackingMode') }}
                </button>
              </div>

              <div class="assets-grid-card__body p-3">
                <!-- Map mode -->
                <template v-if="tracking">
                  <WirepasRouteMap v-if="legsWithCoords.length" :legs="data.journey ?? []" />
                  <div v-else class="text-center text-muted py-4">
                    <i class="bi bi-geo me-2" />{{ $t('components.wirepasAssets.detail.noCoords') }}
                  </div>
                </template>

                <!-- Charts mode -->
                <template v-else>
                  <div v-if="!hasSeries" class="text-center text-muted py-4">
                    <i class="bi bi-bar-chart-line me-2" />{{ $t('components.wirepasAssets.detail.noData') }}
                  </div>
                  <div v-else class="wad-charts row g-3">
                    <div v-if="data.series.rssi.length" class="col-12 col-md-6 wad-chart-block">
                      <div class="wad-chart-title"><i class="bi bi-reception-4" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.rssi') }} [dBm]</div>
                      <LineChart :chart-data="rssiChartData" :options="chartOptions('dBm')" :height="140" />
                    </div>
                    <div v-if="data.series.temperature.length" class="col-12 col-md-6 wad-chart-block">
                      <div class="wad-chart-title"><i class="bi bi-thermometer-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.temperature') }} [°C]</div>
                      <LineChart :chart-data="tempChartData" :options="chartOptions('°C')" :height="140" />
                    </div>
                    <div v-if="data.series.humidity.length" class="col-12 col-md-6 wad-chart-block">
                      <div class="wad-chart-title"><i class="bi bi-droplet-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.humidity') }} [%]</div>
                      <LineChart :chart-data="humChartData" :options="chartOptions('%')" :height="140" />
                    </div>
                    <div v-if="data.series.battery.length" class="col-12 col-md-6 wad-chart-block">
                      <div class="wad-chart-title"><i class="bi bi-battery-half" style="color:#f77f01" /> {{ $t('components.wirepasAssets.detail.battery') }} [V]</div>
                      <LineChart :chart-data="battChartData" :options="chartOptions('V')" :height="140" />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { LineChart } from 'vue-chart-3'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'
import { getWirepasAssetDetail } from '@/crud/wirepas-location'
import { wirepasAgoText, wirepasDurText } from '@/utils/wirepas'
import WirepasJourneySidebar from '@/components/content/WirepasJourneySidebar.vue'
import WirepasRouteMap from '@/components/content/WirepasRouteMap.vue'

Chart.register(...registerables, zoomPlugin)

export default defineComponent({
  name: 'WirepasAssetDetailView',
  components: { LineChart, WirepasJourneySidebar, WirepasRouteMap },
  emits: ['pageName'],

  setup(_, { emit }) {
    const { t } = useI18n()
    const route = useRoute()

    const data = ref<any>(null)
    const tracking = ref(true)
    const loading = ref(true)
    const error = ref<string | null>(null)
    const rangeHours = ref(24)

    // Chart.js grid/tick colors are plain JS options, not CSS — they don't pick up the app's
    // --text-color etc. automatically, so dark mode has to be read explicitly here.
    const isDarkMode = ref(document.body.classList.contains('dark-theme'))
    let darkModeObserver: MutationObserver | null = null

    const rangeOptions = [
      { v: 6,   l: '6h' },
      { v: 24,  l: '24h' },
      { v: 48,  l: '48h' },
      { v: 168, l: '7d' },
    ]

    const fetchData = async () => {
      loading.value = true
      error.value = null
      try {
        const res = await getWirepasAssetDetail(route.params.id as string, rangeHours.value)
        data.value = res.data
        emit('pageName', data.value.name || data.value.serialNumber)
      } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || t('components.wirepasAssets.error')
      } finally {
        loading.value = false
      }
    }

    const setRange = (h: number) => {
      rangeHours.value = h
      fetchData()
    }

    const agoText = (tsSec: number | null) => wirepasAgoText(tsSec, t)

    const durText = (sinceSec: number | null): string => wirepasDurText(sinceSec)

    const legsWithCoords = computed(() =>
      (data.value?.journey ?? []).filter((l: any) => l.lat && l.lng)
    )

    const hasSeries = computed(() =>
      data.value?.series?.temperature?.length ||
      data.value?.series?.humidity?.length ||
      data.value?.series?.battery?.length ||
      data.value?.series?.rssi?.length
    )

    const makeDataset = (series: any[], color: string, label: string, scale = 1) => ({
      datasets: [{
        label,
        data: series.map((p: any) => ({ x: p.ts, y: p.v / scale })),
        borderColor: color,
        borderWidth: 2,
        pointRadius: 2, // keeps sparse/isolated readings visible — a single point draws no line at all
        pointBackgroundColor: color,
        fill: false,
        tension: 0,
      }],
    })

    const PRIMARY = '#f77f01'

    const tempChartData = computed(() => makeDataset(data.value?.series?.temperature ?? [], PRIMARY, '°C'))
    const humChartData  = computed(() => makeDataset(data.value?.series?.humidity ?? [], PRIMARY, '%'))
    const battChartData = computed(() => makeDataset(data.value?.series?.battery ?? [], PRIMARY, 'V', 1000))
    const rssiChartData = computed(() => makeDataset(data.value?.series?.rssi ?? [], PRIMARY, 'dBm'))

    const chartOptions = (unit: string) => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: {
        x: {
          type: 'time' as const,
          time: {
            tooltipFormat: 'DD/MM/YYYY HH:mm',
            displayFormats: { millisecond: 'HH:mm:ss', second: 'HH:mm:ss', minute: 'HH:mm', hour: 'DD/MM HH:mm', day: 'DD/MM' },
          },
          grid: { color: isDarkMode.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
          ticks: { font: { size: 11 }, color: isDarkMode.value ? 'rgba(255,255,255,0.6)' : undefined },
        },
        y: {
          grid: { color: isDarkMode.value ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' },
          ticks: {
            font: { size: 11 },
            color: isDarkMode.value ? 'rgba(255,255,255,0.6)' : undefined,
            callback: (v: any) => `${typeof v === 'number' ? v.toFixed(unit === 'V' ? 2 : 1) : v}${unit}`,
          },
        },
      },
      plugins: {
        // A 'customBackground' plugin registered globally by the vehicle report charts (Voltage.vue
        // etc.) paints a fixed light background on every Chart.js instance in the app, dark mode or
        // not. Opt this chart out rather than touching that shared, unrelated plugin.
        customBackground: false,
        decimation: { enabled: true, algorithm: 'lttb' as const, samples: 500 },
        legend: { display: false },
        tooltip: { mode: 'index' as const, intersect: false },
        zoom: {
          pan: { enabled: true, mode: 'x' as const },
          zoom: { wheel: { enabled: true }, pinch: { enabled: true }, mode: 'x' as const },
        },
      },
    })

    watch(() => route.params.id, fetchData)
    onMounted(() => {
      emit('pageName', '…')
      fetchData()

      darkModeObserver = new MutationObserver(() => {
        isDarkMode.value = document.body.classList.contains('dark-theme')
      })
      darkModeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    })
    onUnmounted(() => darkModeObserver?.disconnect())

    return {
      data, loading, error, rangeHours, rangeOptions,
      hasSeries, tempChartData, humChartData, battChartData, rssiChartData,
      chartOptions, setRange, agoText, durText,
      tracking, legsWithCoords,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

.wad-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 0 16px;
  flex-wrap: wrap;
}

.wad-main { margin-top: 0; }

.wad-toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid rgba(0,0,0,0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: var(--primary-color); color: var(--primary-color); }
  &--active,
  &--active:hover {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: #fff;

    i { color: #fff !important; }
  }
}

.wad-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: var(--primary-color); color: var(--primary-color); }
}

.wad-range-pills { display: flex; gap: 6px; flex-wrap: wrap; }

.wad-kpi-location { min-width: 200px; flex: 2; }

.wad-location-name {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wad-ago { font-size: 22px; font-weight: 800; font-family: monospace; }

.wad-unit { font-size: 14px; font-weight: 400; color: rgba(108,117,125,0.8); margin-left: 2px; }

.wad-status {
  display: inline-block;
  font-size: 18px;
  font-weight: 800;

  &--ok         { color: #3f8f52; }
  &--low_batt   { color: #a07c20; }
  &--dwell      { color: #4870b8; }
  &--in_transit { color: #4870b8; }
  &--departed   { color: #e8833a; }
}

// Layout comes from the Bootstrap .row.g-3 classes on this element in the template —
// two charts per row on md+ screens, one per row on narrower ones.

.wad-chart-block {}

.wad-chart-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(108, 117, 125, 0.9);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

// pills reused from WirepasAssetsView
.wl-pill {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: rgba(var(--primary-color-rgb), 0.4); color: var(--primary-color); }
  &--active { background: var(--primary-color); border-color: var(--primary-color); color: #fff; }
}
</style>
